import React from "react";
import { getFileUrl } from "../../core/config/api";

const fGBP = (v) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v || 0);

// Patek Philippe, Audemars Piguet, Rolex pinned first; everything else A→Z.
const PRIORITY = ["Patek Philippe", "Audemars Piguet", "Rolex"];
const UNKNOWN = "Other";

const orderBrands = (brands) => {
  const present = new Set(brands);
  const pinned = PRIORITY.filter((b) => present.has(b));
  const rest = brands
    .filter((b) => !PRIORITY.includes(b) && b !== UNKNOWN)
    .sort((a, b) => a.localeCompare(b));
  return [...pinned, ...rest, ...(present.has(UNKNOWN) ? [UNKNOWN] : [])];
};

const IChevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const IUser = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ProductThumb = ({ imageId }) => {
  const url = getFileUrl(imageId);
  return (
    <span className="acct-card__thumb">
      {url ? <img src={url} alt="" /> : <IUser />}
    </span>
  );
};

const StockCard = ({ item, variant, onOpen }) => {
  const title = [item.brand, item.model].filter(Boolean).join(" ") || "—";
  const poLabel = item.po_number ? `PO ${item.po_number}` : "PO";
  return (
    <button type="button" className="acct-card" onClick={() => onOpen(item)}>
      <ProductThumb imageId={item.image_file_id} />
      <div className="acct-card__body">
        <span className="acct-card__name" title={title}>{title}</span>
        <div className="acct-card__meta">
          {item.reference && <span className="acct-ref-tag">{item.reference}</span>}
          {item.age_days != null && (
            <span className="acct-card__age">{item.age_days}d in stock</span>
          )}
          {variant === "purchases" && (
            <span className="acct-tag acct-tag--type">
              {item.type === "Consignment"
                ? "Consignment"
                : item.type === "Merchant"
                ? "Merchant"
                : "Stock Purchase"}
            </span>
          )}
        </div>
        {variant === "purchases" && (
          <div className="acct-card__tags">
            {item.via === "sell_request" && (
              <span className={`acct-tag ${item.has_po ? "acct-tag--ok" : "acct-tag--warn"}`} title={item.has_po ? poLabel : undefined}>
                {item.has_po ? "PO generated" : "PO not generated"}
              </span>
            )}
            {item.via === "listing" && (
              <span className={`acct-tag ${item.has_invoice ? "acct-tag--ok" : "acct-tag--warn"}`}>
                {item.has_invoice ? "Invoice uploaded" : "No invoice uploaded"}
              </span>
            )}
            {item.via === "pending_listing" && (
              <span className="acct-tag acct-tag--pending" title="Sell Request — no listing published yet">
                Pending Listing
              </span>
            )}
            {item.via === "pending_listing" && (
              <span className={`acct-tag ${item.has_po ? "acct-tag--ok" : "acct-tag--warn"}`} title={item.has_po ? poLabel : undefined}>
                {item.has_po ? "PO generated" : "PO not generated"}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="acct-card__right">
        <span className="acct-card__cost-label">Cost</span>
        <span className="acct-card__amount">{fGBP(item.cost)}</span>
      </div>
    </button>
  );
};

/**
 * One inventory column (Opening / Stock Purchases / Closing). Items are
 * grouped by brand in the fixed PP→AP→Rolex→A-Z order, each group showing a
 * share bar + count + total cost value.
 *
 * On desktop the three columns sit side by side at a fixed equal height and
 * scroll internally. Below the stacking breakpoint the caller passes
 * `collapsible` and drives `isOpen` / `onToggle` so the columns stack as an
 * accordion with one section open at a time.
 *
 * Props:
 *  - title, badge
 *  - items: stockItem[] | purchaseItem[]
 *  - variant: "stock" | "purchases"
 *  - collapsible, isOpen, onToggle — accordion behaviour (mobile/tablet only)
 */
const StockColumn = ({
  title,
  badge,
  items = [],
  variant = "stock",
  collapsible = false,
  isOpen = true,
  onToggle,
}) => {
  const groups = React.useMemo(() => {
    const byBrand = {};
    items.forEach((it) => {
      const brand = it.brand || UNKNOWN;
      (byBrand[brand] ||= []).push(it);
    });
    const totalCount = items.length || 1;
    return orderBrands(Object.keys(byBrand)).map((brand) => {
      const list = byBrand[brand];
      const value = list.reduce((s, it) => s + (it.cost || 0), 0);
      return {
        brand,
        list,
        count: list.length,
        value,
        pct: Math.round((list.length / totalCount) * 100),
      };
    });
  }, [items]);

  const totalValue = items.reduce((s, it) => s + (it.cost || 0), 0);

  const onOpen = (item) => {
    // Sell-request acquisitions (and pending) open the sell request; every other
    // listing opens its watchcard / Internal Details (where the invoice lives).
    const opensRequest = item.via === "sell_request" || item.via === "pending_listing";
    if (variant === "purchases" && opensRequest && (item.px_request_id || item.request_id)) {
      // sell_request / pending_listing are SELL requests by construction.
      const path = item.px_request_id ? "px-sell" : "sell";
      window.open(`/request/${path}/${item.px_request_id || item.request_id}`, "_blank");
    } else if (item.listing_id) {
      window.open(`/product/${item.listing_id}/edit/product`, "_blank");
    }
  };

  // Collapsed columns don't render their (potentially long) card list at all.
  const showBody = !collapsible || isOpen;

  const heading = (
    <>
      <h2 className="daily-recap__section-title">{title}</h2>
      {badge && <span className="deal-widget__badge deal-widget__badge--green">{badge}</span>}
    </>
  );

  return (
    <div
      className={[
        "deal-widget-outer",
        "acct-col",
        collapsible ? "acct-col--collapsible" : "",
        collapsible && !isOpen ? "acct-col--collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {collapsible ? (
        <button
          type="button"
          className="deal-widget-outer__title-row acct-col__toggle"
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          {heading}
          <span className="acct-col__toggle-meta">
            {items.length} · {fGBP(totalValue)}
          </span>
          <span className="acct-col__chevron" aria-hidden="true"><IChevron /></span>
        </button>
      ) : (
        <div className="deal-widget-outer__title-row">{heading}</div>
      )}

      {showBody && (
        <div className="deal-widget">
          <div className="deal-widget__sub-bar">
            <span className="deal-widget__sub-pill">
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
            <span className="acct-col__total">{fGBP(totalValue)}</span>
          </div>

          {items.length === 0 ? (
            <div className="deal-widget__empty">Nothing in this column</div>
          ) : (
            <div className="acct-groups">
              {groups.map((g) => (
                <div key={g.brand} className="acct-group">
                  <div className="acct-group__head">
                    <span className="acct-group__brand">{g.brand}</span>
                    <span className="acct-group__stats">
                      {g.pct}% · {g.count} · {fGBP(g.value)}
                    </span>
                  </div>
                  <div className="acct-bar">
                    <div className="acct-bar__fill" style={{ width: `${g.pct}%` }} />
                  </div>
                  <div className="deal-widget__list">
                    {g.list.map((item, i) => (
                      <StockCard
                        key={item.listing_id || item.request_id || i}
                        item={item}
                        variant={variant}
                        onOpen={onOpen}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockColumn;
